import type { BackupOperation } from "#backup-operation";
import { validateBackupRelationships } from "#validate-backup-relationships";
import { sealBackup, unsealBackup } from "#backup-codec";
import * as z from "@-/com.npmjs.zod";
import {
  actorDataExportSchema,
  type ActorDataExport,
} from "@-/dev.improve.haniverse.lib.actor-data-transfer";
import { archiveBackupManifestSchema, type ArchiveBackupManifest } from "#backup-types";
import { backupTables } from "#backup-tables";
import { backupStore } from "#backup-store";
import delay from "@-/com.npmjs.delay";

const rowsSchema = z.array(z.record(z.string(), z.unknown()));

/** Let the UI thread breathe between chunks. */
export function backupYield(): Promise<void> {
  return delay(0);
}

export async function writeBackupValue(
  key: string,
  value: unknown,
  operation?: BackupOperation,
  assertCurrent?: () => void,
): Promise<void> {
  operation?.check();
  const sealed = await sealBackup(JSON.stringify(value), key);
  operation?.check();
  assertCurrent?.();
  await backupStore.set(key, sealed, operation?.deadline);
  // Authenticate the persisted bytes before publishing any manifest.
  await readBackupValue(key);
  operation?.check();
  assertCurrent?.();
}

export async function readBackupValue(key: string): Promise<unknown> {
  const raw = await backupStore.get(key);
  if (raw === null) {
    throw new Error("A backup file is missing. Current learning data has not been changed.");
  }
  return JSON.parse(await unsealBackup(raw, key));
}

export async function readBackupManifest(id: string): Promise<ArchiveBackupManifest> {
  const manifest = archiveBackupManifestSchema.parse(await readBackupValue(`manifest/${id}`));
  if (manifest.id !== id) {
    throw new Error("Backup identity mismatch");
  }
  return manifest;
}

/** Validate the ENTIRE archive before the caller is permitted to mutate live data. */
export async function readBackupExport(
  manifest: ArchiveBackupManifest,
  readValue: (key: string) => Promise<unknown> = readBackupValue,
  operation?: BackupOperation,
): Promise<ActorDataExport> {
  const tables: Record<string, Record<string, unknown>[]> = Object.fromEntries(
    backupTables.map((table) => [table.key, []]),
  );
  const seenKeys = new Set<string>();
  for (const [index, chunk] of manifest.chunks.entries()) {
    operation?.report({
      completed: index,
      label: "Reading saved backup",
      total: manifest.chunks.length,
    });
    const table = backupTables.find((item) => item.key === chunk.table);
    if (
      table === undefined ||
      !chunk.key.startsWith(`archive/${manifest.id}/`) ||
      seenKeys.has(chunk.key)
    ) {
      throw new Error("Invalid backup chunk index");
    }
    seenKeys.add(chunk.key);
    const value = await readValue(chunk.key);
    const rows = table.parse(
      manifest.encoding === "sqlite" ? table.decode(rowsSchema.parse(value)) : value,
    );
    operation?.check();
    if (rows.length !== chunk.rows) {
      throw new Error("Backup record count mismatch");
    }
    tables[table.key]?.push(...rows);
    await backupYield();
  }
  const parsed = actorDataExportSchema.parse({
    exportedAt: new Date(manifest.createdAt).toISOString(),
    format: "han-data-export",
    formatVersion: 1,
    tables,
  });
  if (
    Object.values(parsed.tables).reduce((sum, rows) => sum + rows.length, 0) !== manifest.rowCount
  ) {
    throw new Error("Backup record count mismatch");
  }
  validateBackupRelationships(parsed);
  operation?.report({
    completed: manifest.chunks.length,
    label: "Backup verified",
    total: manifest.chunks.length,
  });
  return parsed;
}
